import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { useEffect, useMemo, useState } from 'react'
import ConfettiEffect from '../components/ConfettiEffect'
import OnboardingLayout from '../components/OnboardingLayout'
import { useAuth } from '../hooks/useAuth'
import { apiService } from '../services/api'
import type { OnboardingIntakePayload } from '../services/api'
import { logger } from '../utils/logger'

const STORAGE_KEY = 'onboarding:intake'

const infoCards = [
  {
    icon: '💻',
    title: 'Start Free, Go Premium',
    body: 'Jump in with free courses. Premium features launched monthly.'
  },
  {
    icon: '🛠️',
    title: 'Gamified Labs, Not Just Lessons',
    body: 'Every course leads to hands-on labs. Code, build, ship—not just watch.'
  },
  {
    icon: '📈',
    title: 'Track Your Progress',
    body: 'XP, streaks, levels. Stay consistent even when life gets busy.'
  }
]

type PendingIntake = {
  interests: string[]
  learningGoals: string[]
  skillLevel?: string
}

function normalizeGoals(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    return [value.trim()]
  }
  return []
}

function readPendingIntake(): PendingIntake | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PendingIntake>
    return {
      interests: Array.isArray(parsed.interests) ? parsed.interests : [],
      learningGoals: normalizeGoals(parsed.learningGoals),
      skillLevel: parsed.skillLevel,
    }
  } catch {
    return null
  }
}

export const Route = createFileRoute('/onboarding/step-2')({
  component: OnboardingStep2,
})

function OnboardingStep2() {
  const navigate = useNavigate()
  const { user, oauthComplete, isAuthenticated, loading } = useAuth()
  const pending = useMemo(() => readPendingIntake(), [])
  const [savingTarget, setSavingTarget] = useState<'courses' | 'pricing' | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate({ to: '/signin', replace: true })
    }
  }, [isAuthenticated, loading, navigate])

  useEffect(() => {
    if (!pending) {
      navigate({ to: '/onboarding/step-1', replace: true })
    }
  }, [pending, navigate])

  const handleComplete = async (destination: 'courses' | 'pricing') => {
    if (!pending) return
    setSavingTarget(destination)
    setError(null)

    const payload: OnboardingIntakePayload = {
      interests: pending.interests,
      learningGoals: pending.learningGoals.join(', '),
      skillLevel: pending.skillLevel,
    }

    try {
      const response = await apiService.saveOnboardingIntake(payload)
      if (user) {
        const updatedUser = {
          ...user,
          intakeCompleted: response.intakeCompleted,
          interests: response.interests,
          learningGoals: response.learningGoals ?? undefined,
          skillLevel: response.skillLevel ?? undefined,
        }
        try {
          localStorage.setItem('auth:user', JSON.stringify(updatedUser))
        } catch (storageError) {
          logger.warn('Failed to persist onboarding data to storage', storageError)
        }
        oauthComplete(updatedUser)
      }
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(STORAGE_KEY)
      }
      if (destination === 'courses') {
        navigate({ to: '/courses', search: { newUser: 'true' } })
      } else {
        navigate({ to: '/pricing', search: { plan: 'premium' } })
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save your preferences'
      setError(message)
      logger.error('Unable to save onboarding intake', { message })
    } finally {
      setSavingTarget(null)
    }
  }

  return (
    <>
      <ConfettiEffect />
      <OnboardingLayout
        step={2}
        title="Here's how it works"
        subtitle="Learn, practice, track your progress."
      >
        <motion.section
          className="info-card-grid"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { staggerChildren: 0.12 }
            }
          }}
        >
          {infoCards.map((card) => (
            <motion.article
              key={card.title}
              className="info-card"
              variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }}
            >
              <div className="info-card-icon" aria-hidden>
                {card.icon}
              </div>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </motion.article>
          ))}
        </motion.section>

        <div className="onboarding-cta-stack">
          <button
            className="cta-primary"
            onClick={() => handleComplete('courses')}
            disabled={savingTarget !== null}
          >
            {savingTarget === 'courses' ? 'Saving...' : 'Start Learning for Free'}
          </button>
          <button
            className="cta-upgrade"
            onClick={() => handleComplete('pricing')}
            disabled={savingTarget !== null}
          >
            {savingTarget === 'pricing' ? 'Preparing upgrade...' : 'Upgrade to Premium'}
          </button>
        </div>

        {error && <p className="onboarding-error">{error}</p>}
      </OnboardingLayout>
    </>
  )
}
